<template>
  <div class="legal-agreement">
    <div class="terms-box">
      <h4 class="terms-heading">Terms &amp; Conditions</h4>
      <div class="terms-scroll" ref="termsScroll">
        <ol class="terms-list">
          <li v-for="(item, index) in termsItems" :key="index">
            <template v-if="typeof item === 'string'">{{ item }}</template>
            <template v-else>
              {{ item.text }}
              <ul v-if="item.subItems && item.subItems.length" class="terms-sublist">
                <li v-for="(sub, subIndex) in item.subItems" :key="subIndex">{{ sub }}</li>
              </ul>
            </template>
          </li>
        </ol>
      </div>
    </div>

    <label class="terms-confirm" :class="{ 'has-error': termsError }">
      <input
        type="checkbox"
        class="terms-confirm-input"
        :checked="termsAccepted"
        @change="onTermsChange($event.target.checked)"
      />
      <span class="terms-confirm-text">{{ $t('termsConfirm') }} <span class="req">*</span></span>
    </label>
    <p v-if="termsError" class="field-error">{{ termsError }}</p>

    <div class="signature-section">
      <label class="field-label">Signature <span class="req">*</span></label>
      <div class="canvas-wrap" :class="{ empty: isEmpty() }">
        <canvas ref="canvas"></canvas>
      </div>
      <div class="sig-actions">
        <button type="button" class="btn-clear" @click="clear">Clear</button>
        <span v-if="!hasSig" class="sig-hint">Please sign above to agree</span>
        <span v-else class="sig-ok">Signature captured</span>
      </div>
    </div>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad'
import { resolveTermsItems } from '../constants/termsContent'

var SignaturePadCtor = SignaturePad && (SignaturePad.default || SignaturePad)

export default {
  name: 'LegalAgreement',
  props: {
    value: { type: Boolean, default: false },
    storeId: { type: Number, default: null },
    stores: { type: Array, default: () => [] }
  },
  data () {
    return {
      pad: null,
      hasSig: false,
      termsAccepted: false,
      termsTouched: false
    }
  },
  computed: {
    termsItems () {
      return resolveTermsItems(this.storeId, this.stores)
    },
    termsError () {
      if (this.termsTouched && !this.termsAccepted) {
        return this.$t('termsConfirmRequired')
      }
      return ''
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        this.hasSig = !!val
      }
    },
    storeId () {
      this.resetTermsAcceptance()
    }
  },
  mounted () {
    var self = this
    this.$nextTick(function () {
      try {
        self.initPad()
      } catch (e) {
        console.error('[booking-spa] signature pad', e)
      }
    })
    window.addEventListener('resize', this.resizeCanvas)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeCanvas)
    if (this.$refs && this.$refs.canvas) {
      this.$refs.canvas.removeEventListener('mouseup', this.handleCanvasEnd)
      this.$refs.canvas.removeEventListener('touchend', this.handleCanvasEnd)
      this.$refs.canvas.removeEventListener('pointerup', this.handleCanvasEnd)
    }
  },
  methods: {
    onTermsChange (checked) {
      this.termsAccepted = !!checked
      this.termsTouched = true
      this.$emit('terms-change', this.termsAccepted)
    },
    validate () {
      this.termsTouched = true
      return this.termsAccepted
    },
    resetTermsAcceptance () {
      this.termsAccepted = false
      this.termsTouched = false
      this.$emit('terms-change', false)
    },
    initPad () {
      const Ctor = SignaturePadCtor
      if (!Ctor || typeof Ctor !== 'function') {
        console.error('[booking-spa] SignaturePad not available')
        return
      }
      const canvas = this.$refs.canvas
      if (!canvas || !canvas.parentElement) return
      this.resizeCanvas()
      this.pad = new Ctor(canvas, {
        penColor: '#1a1a2e',
        backgroundColor: '#fff',
        minWidth: 1,
        maxWidth: 2.5,
        onEnd: () => {
          this.handleCanvasEnd()
        }
      })

      // Fallback: some environments don’t call onEnd reliably.
      // We still emit when the user ends a stroke and the pad is non-empty.
      canvas.addEventListener('mouseup', this.handleCanvasEnd)
      canvas.addEventListener('touchend', this.handleCanvasEnd)
      canvas.addEventListener('pointerup', this.handleCanvasEnd)
    },
    handleCanvasEnd () {
      if (!this.pad) return
      // If user just taps without drawing, keep it empty.
      if (this.pad.isEmpty && this.pad.isEmpty()) {
        this.hasSig = false
        return
      }
      this.hasSig = true
      if (this.$refs && this.$refs.canvas) {
        this.$emit('signature', this.$refs.canvas.toDataURL('image/png'))
      }
    },
    resizeCanvas () {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const wrap = canvas.parentElement
      if (!wrap) return
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      const w = Math.max(wrap.clientWidth || 0, 280)
      const h = Math.max(wrap.clientHeight || 0, 140)
      canvas.width = w * ratio
      canvas.height = h * ratio
      canvas.style.width = w + 'px'
      canvas.style.height = h + 'px'
      const ctx = canvas.getContext('2d')
      // Ensure we never accumulate transforms across resizes.
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(ratio, ratio)
      if (this.pad) this.pad.clear()
    },
    clear () {
      if (this.pad) this.pad.clear()
      this.hasSig = false
      this.$emit('clear')
    },
    isEmpty () {
      return this.pad ? this.pad.isEmpty() : true
    },
    toBlob (cb) {
      if (!this.$refs.canvas) return cb(null)
      this.$refs.canvas.toBlob(cb, 'image/png')
    }
  }
}
</script>

<style scoped>
.legal-agreement { margin-bottom: 20px; }

.terms-box {
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.terms-heading {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .5px;
  padding: 10px 14px;
  background: #f5f7fa;
  border-bottom: 1px solid #e8ecf1;
  color: #1a1a2e;
}

.terms-scroll {
  max-height: 280px;
  overflow-y: auto;
  padding: 14px;
  -webkit-overflow-scrolling: touch;
}

.terms-list {
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.65;
  color: #374151;
}
.terms-list li { margin-bottom: 8px; }
.terms-list li:last-child { margin-bottom: 0; }
.terms-sublist {
  margin: 8px 0 0;
  padding-left: 18px;
  list-style: disc;
}
.terms-sublist li { margin-bottom: 4px; }
.terms-sublist li:last-child { margin-bottom: 0; }

.terms-confirm {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  cursor: pointer;
}
.terms-confirm.has-error { border-color: #ef4444; background: #fef2f2; }
.terms-confirm-input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #1e336f;
}
.terms-confirm-text {
  font-size: 14px;
  line-height: 1.5;
  color: #1e336f;
}
.field-error { margin: -8px 0 14px; font-size: 12px; color: #ef4444; }

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 6px;
}

.req { color: #ef4444; }

.canvas-wrap {
  width: 100%;
  height: 160px;
  border: 2px dashed #e8ecf1;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color .2s;
}
.canvas-wrap.empty { border-color: #d1d5db; }

.canvas-wrap canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.sig-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.btn-clear {
  font-size: 13px;
  font-weight: 600;
  color: #ef4444;
  background: none;
  border: 1px solid #ef4444;
  border-radius: 6px;
  padding: 5px 14px;
  cursor: pointer;
  transition: background .2s, color .2s;
}
.btn-clear:hover { background: #ef4444; color: #fff; }

.sig-hint { font-size: 12px; color: #9ca3af; }
.sig-ok { font-size: 12px; color: #10b981; font-weight: 600; }
</style>



// WEBPACK FOOTER //
// src/components/LegalAgreement.vue